import * as wjcGrid from "@grapecity/wijmo.grid"
import * as wjcGridXlsx from "@grapecity/wijmo.grid.xlsx"
import * as wjcXlsx from "@grapecity/wijmo.xlsx"
import * as wjcGridPdf from "@grapecity/wijmo.grid.pdf"
import dayjs from "dayjs"
import { TInvoiceRegisterListGetRes } from "../../service/billing/invoice/TInvoice"
import { TPaymentRegisterListGetRes } from "../../service/billing/payment/TPayment"
import { TOsiraseGetListRes } from "../../service/master/osirase/TOsirase"
import {
  TTenkoDetailInfoGetRes,
  TTenkoListInfoGetRes,
} from "../../service/truckcontrol/tenko/TTenko"

//TODO: 日付と日時に分ける

//#region [abstract]

// MEMO: 違う処理の列を作りたい場合、プロパティを増やすこと
export type TExportColumns<T extends string> = {
  /**
   * 日付のカラム
   */
  DateColumns?: T[]
  /**
   * 日時のカラム
   */
  DateTimeColumns?: T[]
  /**
   * 月日のカラム
   */
  MonthDateColumns?: T[]
  /**
   * 時刻のカラム
   */
  TimeColumns?: T[]
}

/**
 * excel上でカラムに対して日付フォーマットを適用します
 * @param binding
 * @param columns
 * @param xlsxCell
 * @param dayJsFormat
 */
const excelDateColumnFormatter =
  (dayJsFormat: string) =>
  (
    binding: string | null,
    columns: string[] | null | undefined,
    xlsxCell: wjcXlsx.IWorkbookCell
  ) => {
    if (columns == null) return
    if (binding && columns.includes(binding)) {
      if (xlsxCell.value && dayjs(xlsxCell.value).isValid()) {
        const value = xlsxCell.value
        xlsxCell.value = dayjs(value).format(dayJsFormat)
      }
    }
  }

const excelTimeSpanColumnFormatter = (
  binding: string | null,
  columns: string[] | null | undefined,
  xlsxCell: wjcXlsx.IWorkbookCell
) => {
  if (columns == null) return
  if (binding && columns.includes(binding)) {
    if (xlsxCell.value != null && typeof xlsxCell.value === "string") {
      const value = xlsxCell.value
      if (value.includes(":")) {
        if (dayjs(xlsxCell.value).isValid()) {
          const value = xlsxCell.value
          xlsxCell.value = dayjs(value).format("HH:mm")
        }
      } else {
        if (!Number.isNaN(parseInt(value))) {
          xlsxCell.value = dayjs(
            dayjs("2023/01/01 00:00:00").add(parseInt(value), "ms")
          ).format("HH:mm")
        }
      }
    }
  }
}

const excel日付ColumnFormatter = excelDateColumnFormatter("YYYY/MM/DD")
const excel日時ColumnFormatter = excelDateColumnFormatter("YYYY/MM/DD HH:mm:ss")
const excel月日ColumnFormatter = excelDateColumnFormatter("MM/DD")
const excel時刻ColumnFormatter = excelTimeSpanColumnFormatter

export const abstractExcelFormat: <T extends string>(
  exportColumns: TExportColumns<T>
) => (args: wjcGridXlsx.XlsxFormatItemEventArgs) => void =
  (exportColumns) => (args) => {
    const p = args.panel
    const col = args.col
    const xlsxCell = args.xlsxCell
    const binding = p.columns[col].binding

    if (p.cellType === wjcGrid.CellType.Cell) {
      excel日付ColumnFormatter(binding, exportColumns.DateColumns, xlsxCell)
      excel日時ColumnFormatter(binding, exportColumns.DateTimeColumns, xlsxCell)
      excel月日ColumnFormatter(
        binding,
        exportColumns.MonthDateColumns,
        xlsxCell
      )
      excel時刻ColumnFormatter(binding, exportColumns.TimeColumns, xlsxCell)
    }
  }

const pdfDateColumnFormatter =
  (dayJsFormat: string) =>
  (
    binding: string | null,
    columns: string[] | null | undefined,
    args: wjcGridPdf.PdfFormatItemEventArgs
  ) => {
    if (columns == null) return
    if (binding && columns.includes(binding)) {
      if (args.data && dayjs(args.data).isValid()) {
        const value = args.data
        args.data = dayjs(value).format(dayJsFormat)
      }
    }
  }

const pdfTimeSpanColumnFormatter = (
  binding: string | null,
  columns: string[] | null | undefined,
  args: wjcGridPdf.PdfFormatItemEventArgs
) => {
  if (columns == null) return
  if (binding && columns.includes(binding)) {
    if (args.data != null && typeof args.data === "string") {
      const value = args.data
      if (value.includes(":")) {
        if (dayjs(value).isValid()) {
          args.data = dayjs(value).format("HH:mm")
        }
      } else {
        if (!Number.isNaN(parseInt(value))) {
          args.data = dayjs(
            dayjs("2023/01/01 00:00:00").add(parseInt(value), "ms")
          ).format("HH:mm")
        }
      }
    }
  }
}

const pdf日付ColumnFormatter = pdfDateColumnFormatter("YYYY/MM/DD")
const pdf日時ColumnFormatter = pdfDateColumnFormatter("YYYY/MM/DD HH:mm:ss")
const pdf月日ColumnFormatter = pdfDateColumnFormatter("MM/DD")
const pdf時刻ColumnFormatter = pdfTimeSpanColumnFormatter

export const abstractPdfFormat: <T extends string>(
  exportColumns: TExportColumns<T>
) => (args: wjcGridPdf.PdfFormatItemEventArgs) => void =
  (exportColumns) => (args) => {
    const p = args.panel
    const col = args.col
    const binding = p.columns[col].binding

    if (p.cellType === wjcGrid.CellType.Cell) {
      pdf日付ColumnFormatter(binding, exportColumns.DateColumns, args)
      pdf日時ColumnFormatter(binding, exportColumns.DateTimeColumns, args)
      pdf月日ColumnFormatter(binding, exportColumns.MonthDateColumns, args)
      pdf時刻ColumnFormatter(binding, exportColumns.TimeColumns, args)
    }
  }

//#endregion

// 点呼一覧
const 点呼一覧columns: TExportColumns<keyof TTenkoListInfoGetRes> = {
  MonthDateColumns: [
    "積日",
    "卸日",
    "始業実点呼日",
    "中間実点呼日",
    "終業実点呼日",
  ],
  TimeColumns: ["始業点呼時刻", "中間点呼時刻", "終業点呼時刻"],
}
export const tenkoListExcelFormat = abstractExcelFormat(点呼一覧columns)
export const tenkoListPdfFormat = abstractPdfFormat(点呼一覧columns)

// 点呼一覧
const 点呼結果一覧columns: TExportColumns<keyof TTenkoDetailInfoGetRes> = {
  MonthDateColumns: ["点呼日", "実点呼日"],
  TimeColumns: ["点呼時刻"],
}
export const tenkoResultListExcelFormat =
  abstractExcelFormat(点呼結果一覧columns)
export const tenkoResultListPdfFormat = abstractPdfFormat(点呼結果一覧columns)

// 請求明細一覧
const 請求明細columns: TExportColumns<keyof TInvoiceRegisterListGetRes> = {
  DateColumns: ["積日", "卸日", "請求計上日"],
}
export const invoiceListExcelFormat = abstractExcelFormat(請求明細columns)
export const invoiceListPdfFormat = abstractPdfFormat(請求明細columns)

// 支払明細一覧
const 支払明細columns: TExportColumns<keyof TPaymentRegisterListGetRes> = {
  DateColumns: ["積日", "卸日", "支払計上日"],
}
export const paymentListExcelFormat = abstractExcelFormat(支払明細columns)
export const paymentListPdfFormat = abstractPdfFormat(支払明細columns)

// お知らせ
const お知らせcolumns: TExportColumns<keyof TOsiraseGetListRes> = {
  DateColumns: ["掲載開始日", "掲載終了日"],
  DateTimeColumns: [
    "更新日時", // 時間付き
  ],
}
export const osiraseExcelFormat = abstractExcelFormat(お知らせcolumns)
export const osirasePdfFormat = abstractPdfFormat(お知らせcolumns)
